import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import moment from 'moment';
import { FaCar, FaPhoneSquare, FaCommentDots } from 'react-icons/fa';

import axios from 'helpers/axios';
import config from 'helpers/config';
import Inspection from 'models/InspectionModel';
import InspectionTaskStatus from 'models/types/InspectionTaskStatus';
import FullPhotoView, { PhotoFileModel } from 'components/common/FullPhotoView';

// Full Tailwind class strings (no interpolation) so they survive purge.
const statusMeta = (rating: InspectionTaskStatus | null) => {
    switch (rating) {
        case InspectionTaskStatus.OK:
            return { label: 'OK', dot: 'bg-green-500', text: 'text-green-700', bg: 'bg-green-50', border: 'border-green-200' };
        case InspectionTaskStatus.NeedsAttention:
            return {
                label: 'Needs attention',
                dot: 'bg-yellow-500',
                text: 'text-yellow-700',
                bg: 'bg-yellow-50',
                border: 'border-yellow-200',
            };
        case InspectionTaskStatus.NeedsRepair:
            return { label: 'Needs repair', dot: 'bg-red-500', text: 'text-red-700', bg: 'bg-red-50', border: 'border-red-200' };
        case InspectionTaskStatus.NotApplicable:
            return { label: 'N/A', dot: 'bg-gray-300', text: 'text-gray-500', bg: 'bg-gray-50', border: 'border-gray-200' };
        default:
            return { label: 'Not inspected', dot: 'bg-gray-300', text: 'text-gray-500', bg: 'bg-white', border: 'border-gray-200' };
    }
};

const PublicInspectionPage: React.FC = () => {
    const { inspectionId } = useParams<{ inspectionId: string }>();
    const [inspection, setInspection] = useState<Inspection | undefined>();
    const [repairOrder, setRepairOrder] = useState<any>();
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(false);
    const [viewer, setViewer] = useState<{ photos: PhotoFileModel[]; index: number } | null>(null);

    const toPhotoFiles = (files: { id: string; fileName: string }[]): PhotoFileModel[] =>
        (files || []).map((f) => ({
            id: f.id,
            fileName: f.fileName,
            photoSourceURL: `${config.fileRootURL}/images/${f.fileName}`,
        }));

    useEffect(() => {
        axios
            .get(`${config.apiUrl}/inspection/public/${inspectionId}`)
            .then((res) => {
                const insp: Inspection = res.data;
                setInspection(insp);
                if (insp?.repairorderId) {
                    return axios
                        .get(`${config.apiUrl}/repairOrder/public/${insp.repairorderId}`)
                        .then((roRes) => setRepairOrder(roRes.data))
                        .catch(() => undefined);
                }
            })
            .catch(() => setError(true))
            .finally(() => setIsLoading(false));
    }, [inspectionId]);

    if (isLoading) return <div className="p-10 text-center text-gray-500">Loading inspection…</div>;
    if (error || !inspection)
        return <div className="p-10 text-center text-gray-600 font-semibold">Inspection not found.</div>;

    const countByStatus = (status: InspectionTaskStatus) =>
        inspection.groups?.reduce((acc, g) => acc + (g.tasks?.filter((t) => t.rating === status).length || 0), 0) || 0;

    const tenant = repairOrder?.tenant;
    const vehicle = repairOrder?.vehicle;

    return (
        <div className="min-h-screen bg-gray-50">
            {viewer && (
                <FullPhotoView
                    photoFiles={viewer.photos}
                    initialPhotoIndex={viewer.index}
                    onClose={() => setViewer(null)}
                />
            )}
            <div className="bg-gradient-to-r from-blue-700 to-blue-500 px-6 py-4 text-white">
                <h1 className="text-lg font-semibold">{tenant?.businessName || 'Vehicle Inspection'}</h1>
                {tenant?.tenantSettings?.phoneNumber && (
                    <a href={`tel:${tenant.tenantSettings.phoneNumber}`} className="text-sm">
                        <FaPhoneSquare className="inline mr-1 align-baseline" /> {tenant.tenantSettings.phoneNumber}
                    </a>
                )}
            </div>

            <div className="max-w-2xl mx-auto p-4">
                <div className="mb-4">
                    <h2 className="text-2xl font-semibold">{inspection.name || 'Inspection'}</h2>
                    {vehicle && (
                        <div className="text-gray-600">
                            <FaCar className="inline mr-2 align-baseline" />
                            {vehicle.year} {vehicle.make} {vehicle.model}
                        </div>
                    )}
                    <div className="text-sm text-gray-500">
                        {inspection.isCompleted
                            ? `Completed ${moment(inspection.completionDate).format('MMMM D, YYYY')}`
                            : 'In progress'}
                    </div>
                </div>

                <div className="grid grid-cols-3 gap-3 mb-6">
                    <div className="rounded-lg border p-3 text-center bg-green-50 border-green-200">
                        <div className="text-2xl font-bold text-green-700">{countByStatus(InspectionTaskStatus.OK)}</div>
                        <div className="text-xs text-green-700 font-semibold">OK</div>
                    </div>
                    <div className="rounded-lg border p-3 text-center bg-yellow-50 border-yellow-200">
                        <div className="text-2xl font-bold text-yellow-700">
                            {countByStatus(InspectionTaskStatus.NeedsAttention)}
                        </div>
                        <div className="text-xs text-yellow-700 font-semibold">Attention</div>
                    </div>
                    <div className="rounded-lg border p-3 text-center bg-red-50 border-red-200">
                        <div className="text-2xl font-bold text-red-700">
                            {countByStatus(InspectionTaskStatus.NeedsRepair)}
                        </div>
                        <div className="text-xs text-red-700 font-semibold">Needs repair</div>
                    </div>
                </div>

                {inspection.groups?.map((group) => (
                    <div key={group.id} className="mb-5">
                        <h3 className="font-semibold text-gray-800 mb-2">{group.name}</h3>
                        <div className="space-y-2">
                            {group.tasks?.map((item) => {
                                const meta = statusMeta(item.rating);
                                return (
                                    <div key={item.id} className={`border rounded-lg p-3 ${meta.bg} ${meta.border}`}>
                                        <div className="flex items-center justify-between">
                                            <span className="font-medium text-gray-800">{item.description}</span>
                                            <span className={`text-xs font-semibold flex items-center whitespace-nowrap ml-3 ${meta.text}`}>
                                                <span className={`w-2.5 h-2.5 rounded-full mr-1.5 ${meta.dot}`}></span>
                                                {meta.label}
                                            </span>
                                        </div>
                                        {item.finding && (
                                            <div className="mt-2 flex items-start bg-white border-l-4 border-blue-400 rounded px-3 py-2">
                                                <FaCommentDots className="text-blue-400 mt-0.5 mr-2 flex-shrink-0" />
                                                <div>
                                                    <div className="text-[11px] font-semibold text-blue-500 uppercase tracking-wide">
                                                        Technician note
                                                    </div>
                                                    <div className="text-sm text-gray-700">{item.finding}</div>
                                                </div>
                                            </div>
                                        )}
                                        {!!item.files?.length && (
                                            <div className="flex flex-wrap gap-2 mt-2">
                                                {item.files
                                                    .filter((f) => f.type !== 'video')
                                                    .map((file, fileIndex, imgs) => (
                                                        <img
                                                            key={file.id}
                                                            src={`${config.fileRootURL}/images/${file.fileName}`}
                                                            alt="inspection"
                                                            className="w-20 h-20 object-cover rounded-md border clickable hover:opacity-90"
                                                            onClick={() =>
                                                                setViewer({ photos: toPhotoFiles(imgs), index: fileIndex })
                                                            }
                                                        />
                                                    ))}
                                                {item.files
                                                    .filter((f) => f.type === 'video')
                                                    .map((file) => (
                                                        <video
                                                            key={file.id}
                                                            src={`${config.fileRootURL}/videos/${file.fileName}`}
                                                            controls
                                                            className="w-full max-w-md h-56 rounded-md border bg-black"
                                                        />
                                                    ))}
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                ))}

                <div className="text-center text-xs text-gray-400 py-6">
                    Performed by{' '}
                    {inspection.completedBy?.firstName || inspection.createdBy?.firstName || 'the shop'}
                </div>
            </div>
        </div>
    );
};

export default PublicInspectionPage;
